import { LatLngBounds } from 'leaflet';

// All dimensions in millimeters
export interface PaperDimensions {
    width: number;
    height: number;
}

export interface PrintMargins {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

export interface TemplateRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface TemplateElement {
    id: string;
    type: 'rect' | 'text' | 'line' | 'scaleBar' | 'northArrow' | 'legend';
    rect: TemplateRect;
    style?: {
        strokeColor?: string;
        strokeWidth?: number;
        fillColor?: string;
        fontSize?: number;
        fontFamily?: string;
        textAlign?: 'left' | 'center' | 'right';
    };
    content?: string;
}

export interface PaperMapTemplate {
    name: string;
    description: string;
    pageSize: PaperDimensions;
    printMargins: PrintMargins;
    // The map viewport area on the page (where the map image goes)
    mapViewport: TemplateRect;
    // Additional template elements (title, scale bar, etc.)
    elements: TemplateElement[];
}

// Standard paper sizes in mm
export const PAPER_SIZES: Record<string, PaperDimensions> = {
    'A4': { width: 210, height: 297 },
    'A3': { width: 297, height: 420 },
    'A2': { width: 420, height: 594 },
    'A1': { width: 594, height: 841 },
    'A0': { width: 841, height: 1189 },
};

// Default print margins (5mm on all sides)
export const DEFAULT_PRINT_MARGINS: PrintMargins = {
    top: 5,
    right: 5,
    bottom: 5,
    left: 5,
};

/**
 * Creates a simple full-page map template.
 * The map viewport fills the entire page minus print margins.
 */
export function createFullPageTemplate(
    pageSize: keyof typeof PAPER_SIZES,
    orientation: 'portrait' | 'landscape',
    margins: PrintMargins = DEFAULT_PRINT_MARGINS
): PaperMapTemplate {
    const baseDims = PAPER_SIZES[pageSize];
    const pageDims: PaperDimensions = orientation === 'landscape'
        ? { width: baseDims.height, height: baseDims.width }
        : { width: baseDims.width, height: baseDims.height };

    // Map viewport is full page minus margins
    const mapViewport: TemplateRect = {
        x: margins.left,
        y: margins.top,
        width: pageDims.width - margins.left - margins.right,
        height: pageDims.height - margins.top - margins.bottom,
    };

    return {
        name: `${pageSize} ${orientation} - Full Page Map`,
        description: 'Map fills the entire printable area',
        pageSize: pageDims,
        printMargins: margins,
        mapViewport,
        elements: [
            // Scale bar in bottom-left corner of map viewport
            {
                id: 'scaleBar',
                type: 'scaleBar',
                rect: {
                    x: margins.left + 5,
                    y: pageDims.height - margins.bottom - 15,
                    width: 50,
                    height: 10,
                },
            },
            // North arrow in top-right corner of map viewport
            {
                id: 'northArrow',
                type: 'northArrow',
                rect: {
                    x: pageDims.width - margins.right - 15,
                    y: margins.top + 5,
                    width: 10,
                    height: 15,
                },
            },
        ],
    };
}

/**
 * Creates a template with title block at the top.
 */
export function createTemplateWithTitle(
    pageSize: keyof typeof PAPER_SIZES,
    orientation: 'portrait' | 'landscape',
    title: string,
    margins: PrintMargins = DEFAULT_PRINT_MARGINS
): PaperMapTemplate {
    const baseDims = PAPER_SIZES[pageSize];
    const pageDims: PaperDimensions = orientation === 'landscape'
        ? { width: baseDims.height, height: baseDims.width }
        : { width: baseDims.width, height: baseDims.height };

    const titleBlockHeight = 15;

    // Map viewport is below title block
    const mapViewport: TemplateRect = {
        x: margins.left,
        y: margins.top + titleBlockHeight,
        width: pageDims.width - margins.left - margins.right,
        height: pageDims.height - margins.top - margins.bottom - titleBlockHeight,
    };

    return {
        name: `${pageSize} ${orientation} - With Title`,
        description: 'Map with title block at top',
        pageSize: pageDims,
        printMargins: margins,
        mapViewport,
        elements: [
            // Title block
            {
                id: 'titleBlock',
                type: 'rect',
                rect: {
                    x: margins.left,
                    y: margins.top,
                    width: pageDims.width - margins.left - margins.right,
                    height: titleBlockHeight,
                },
                style: {
                    strokeColor: '#000000',
                    strokeWidth: 0.5,
                },
            },
            // Title text
            {
                id: 'titleText',
                type: 'text',
                rect: {
                    x: pageDims.width / 2,
                    y: margins.top + titleBlockHeight / 2 + 2,
                    width: 0,
                    height: 0,
                },
                style: {
                    fontSize: 12,
                    fontFamily: 'Helvetica',
                    textAlign: 'center',
                },
                content: title,
            },
            // Scale bar
            {
                id: 'scaleBar',
                type: 'scaleBar',
                rect: {
                    x: margins.left + 5,
                    y: pageDims.height - margins.bottom - 15,
                    width: 50,
                    height: 10,
                },
            },
            // North arrow
            {
                id: 'northArrow',
                type: 'northArrow',
                rect: {
                    x: pageDims.width - margins.right - 15,
                    y: margins.top + titleBlockHeight + 5,
                    width: 10,
                    height: 15,
                },
            },
        ],
    };
}

/**
 * Viewport bounds data to pass to renderer
 */
export interface ViewportBounds {
    latLngBounds: LatLngBounds;
    // Ground distance represented by the viewport
    groundWidthMeters: number;
    groundHeightMeters: number;
}

/**
 * Calculate the ground dimensions for a template's map viewport at a given scale
 */
export function calculateGroundDimensions(
    template: PaperMapTemplate,
    scale: number
): { widthMeters: number; heightMeters: number } {
    // Paper dimension (mm) / 1000 = meters at 1:1, then multiply by scale
    const widthMeters = (template.mapViewport.width / 1000) * scale;
    const heightMeters = (template.mapViewport.height / 1000) * scale;
    return { widthMeters, heightMeters };
}
